/* ==========================================================================
   Unified media skeleton controller
   --------------------------------------------------------------------------
   Adds a premium shimmer placeholder + fade-in to every lazy-loaded media
   surface (product cards, PDP gallery, banners, carousels) from ONE place.
   - Config-driven: each entry maps a media selector to the frame that should
     shimmer. Add a surface by adding one line to MEDIA_TARGETS.
   - Generic opt-in: any <img|video|iframe data-skeleton> is handled too.
   - Auto-initialises dynamically injected media (AJAX cards, cart drawer,
     quick-add / upgrade drawers, predictive search, infinite scroll) via a
     MutationObserver, and Shopify theme-editor section reloads.
   - Cache-safe (no flash for already-loaded media) and self-healing (safety
     timeout clears a stuck skeleton). Leaves the DOM pristine once revealed.
   Styles: assets/skeleton-media.css
   ========================================================================== */
(function () {
  "use strict";

  // { media: <selector for the media element>, frame: <closest() selector for
  //   the space-reserving wrapper, or null to use the media's parent> }
  var MEDIA_TARGETS = [
    // Product cards (used across collections, featured, search, recommendations)
    { media: ".card-product__image--featured", frame: ".card-product__image-wrapper" },
    // PDP gallery — main + fullscreen slides, and thumbnails
    { media: ".gallery-slide .main-image", frame: ".gallery-slide" },
    { media: ".gallery-slide .video-container video", frame: ".gallery-slide" },
    { media: ".thumbnail-item img", frame: ".thumbnail-item" },
    // Banner / media sections
    { media: ".media-banner__media", frame: ".media-banner__media-wrapper" },
    { media: ".image-with-title__image img", frame: ".image-with-title__image" },
    { media: ".content-media-split__media", frame: ".content-media-split__media-wrapper" },
    // Carousel / gallery sections
    { media: ".morgan-gallery__card-media img", frame: ".morgan-gallery__card-media" },
    { media: ".swiper-slider__media-wrap img, .swiper-slider__media-wrap video", frame: ".swiper-slider__media-wrap" },
    { media: ".hero-media-carousel__media", frame: ".hero-media-carousel__slide-link" },
    // Generic opt-in for anything else
    { media: "img[data-skeleton], video[data-skeleton], iframe[data-skeleton]", frame: null }
  ];

  var SAFETY_MS = 8000; // never leave a shimmer stuck if a load event never fires
  var CLEANUP_MS = 650; // remove helper classes after the fade completes

  function reveal(media, frame, timer) {
    if (timer) clearTimeout(timer);
    media.classList.add("skeleton-media--loaded");
    if (frame) {
      frame.classList.remove("is-loading");
      frame.classList.add("is-loaded");
      frame.removeAttribute("aria-busy");
    }
    // Return the DOM to a pristine state once the fade has run.
    setTimeout(function () {
      media.classList.remove("skeleton-media", "skeleton-media--loaded");
      if (frame) frame.classList.remove("media-skeleton", "is-loading", "is-loaded");
    }, CLEANUP_MS);
  }

  function isReady(el) {
    if (el.tagName === "IMG") return el.complete && el.naturalHeight !== 0;
    if (el.tagName === "VIDEO") return el.readyState >= 2; // HAVE_CURRENT_DATA
    return false;
  }

  function wire(media, frameSel) {
    var tag = media.tagName;
    if (tag !== "IMG" && tag !== "VIDEO" && tag !== "IFRAME") return;
    if (media.dataset.skeletonInit) return;
    media.dataset.skeletonInit = "1";

    var frame = frameSel ? media.closest(frameSel) : media.parentElement;
    if (frame) frame.classList.add("media-skeleton");
    media.classList.add("skeleton-media");

    // Already loaded (e.g. from cache or an eager first slide): no flash.
    if (isReady(media)) {
      reveal(media, frame, null);
      return;
    }

    if (frame) {
      frame.classList.add("is-loading");
      frame.setAttribute("aria-busy", "true");
    }

    var done = false;
    var timer = setTimeout(function () {
      if (!done) { done = true; reveal(media, frame, null); }
    }, SAFETY_MS);

    function finish() {
      if (done) return;
      done = true;
      reveal(media, frame, timer);
    }

    if (tag === "VIDEO") {
      media.addEventListener("loadeddata", finish, { once: true });
      media.addEventListener("canplay", finish, { once: true });
    } else {
      media.addEventListener("load", finish, { once: true });
    }
    // On error, still reveal so the alt text / broken state is visible.
    media.addEventListener("error", finish, { once: true });
  }

  function scan(root) {
    root = root || document;
    for (var i = 0; i < MEDIA_TARGETS.length; i++) {
      var t = MEDIA_TARGETS[i];
      var nodes = root.querySelectorAll(t.media);
      for (var j = 0; j < nodes.length; j++) wire(nodes[j], t.frame);
    }
  }

  var scheduled = false;
  function scheduleScan() {
    if (scheduled) return;
    scheduled = true;
    (window.requestAnimationFrame || window.setTimeout)(function () {
      scheduled = false;
      scan(document);
    });
  }

  var MEDIA_MATCH = "img, video, iframe, [data-skeleton]";
  function nodeHasMedia(node) {
    if (node.nodeType !== 1) return false;
    return (
      (node.matches && node.matches(MEDIA_MATCH)) ||
      (node.querySelector && node.querySelector(MEDIA_MATCH))
    );
  }

  function init() {
    scan(document);

    if (window.MutationObserver) {
      var mo = new MutationObserver(function (mutations) {
        for (var i = 0; i < mutations.length; i++) {
          var added = mutations[i].addedNodes;
          for (var j = 0; j < added.length; j++) {
            if (nodeHasMedia(added[j])) {
              scheduleScan();
              return;
            }
          }
        }
      });
      mo.observe(document.body, { childList: true, subtree: true });
    }

    // Shopify theme editor
    document.addEventListener("shopify:section:load", scheduleScan);
    document.addEventListener("shopify:section:reorder", scheduleScan);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }

  // Public API: call SkeletonMedia.scan(container) after custom AJAX renders.
  window.SkeletonMedia = { scan: scan };
})();
